import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  CheckCircle,
  Search,
  Build,
} from '@mui/icons-material';
import { scanMissingBatches, repairMissingBatches } from '../firebase/batchRepairService';

const MigrationTool = ({ currentUser }) => {
  const [repairScanning, setRepairScanning] = useState(false);
  const [repairRepairing, setRepairRepairing] = useState(false);
  const [missingBatches, setMissingBatches] = useState(null);
  const [repairResult, setRepairResult] = useState(null);
  const [repairStep, setRepairStep] = useState('');

  /**
   * Single-click flow:
   *  1. Scan all transfers → collect missing batches
   *  2. If any missing → automatically write them back
   *  3. Re-scan to confirm everything is clear
   *  4. Show final result
   */
  const handleRepairAll = async () => {
    setRepairScanning(true);
    setRepairRepairing(false);
    setMissingBatches(null);
    setRepairResult(null);

    try {
      // Phase 1: Scan
      setRepairStep('Scanning all stock transfers…');
      const scanResult = await scanMissingBatches();

      if (!scanResult.success) {
        setMissingBatches([]);
        setRepairResult({ success: false, repaired: 0, failed: [`Scan failed: ${scanResult.error}`] });
        return;
      }

      setMissingBatches(scanResult.missing);

      if (scanResult.missing.length === 0) {
        setRepairResult({ success: true, repaired: 0, failed: [], nothingMissing: true });
        return;
      }

      // Phase 2: Repair
      setRepairRepairing(true);
      setRepairStep(`Found ${scanResult.missing.length} missing batch${scanResult.missing.length > 1 ? 'es' : ''}. Writing back to inventory…`);
      const repairRes = await repairMissingBatches(scanResult.missing);

      // Phase 3: Re-scan to verify
      setRepairStep('Verifying repair…');
      const rescan = await scanMissingBatches();
      setMissingBatches(rescan.success ? rescan.missing : []);

      setRepairResult({
        ...repairRes,
        stillMissing: rescan.success ? rescan.missing.length : null,
      });
    } catch (err) {
      setRepairResult({ success: false, repaired: 0, failed: [err.message] });
    } finally {
      setRepairScanning(false);
      setRepairRepairing(false);
      setRepairStep('');
    }
  };

  const handleScanOnly = async () => {
    setRepairScanning(true);
    setRepairRepairing(false);
    setMissingBatches(null);
    setRepairResult(null);
    setRepairStep('Scanning…');
    try {
      const scanResult = await scanMissingBatches();
      setMissingBatches(scanResult.success ? scanResult.missing : []);
      if (!scanResult.success) {
        setRepairResult({ success: false, repaired: 0, failed: [`Scan failed: ${scanResult.error}`] });
      }
    } catch (err) {
      setMissingBatches([]);
      setRepairResult({ success: false, repaired: 0, failed: [err.message] });
    } finally {
      setRepairScanning(false);
      setRepairStep('');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
        Admin Tools
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Advanced utilities for data repair and maintenance. All operations are safe to run
        anytime — they only append or fix, never delete existing data.
      </Typography>

      <Divider sx={{ mb: 4 }} />

      {/* ── Inventory Batch Repair ─────────────────────────────────────────── */}
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
        Inventory Batch Repair
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Scans every stock transfer record, cross-checks against inventory batches,
        and automatically re-records any missing ones in a single click.
      </Typography>

      {/* Progress indicator */}
      {(repairScanning || repairRepairing) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            {repairStep}
          </Typography>
        </Box>
      )}

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={(repairScanning || repairRepairing) ? <CircularProgress size={18} color="inherit" /> : <Build />}
          onClick={handleRepairAll}
          disabled={repairScanning || repairRepairing}
        >
          {(repairScanning || repairRepairing) ? 'Running…' : 'Scan & Repair Missing Batches'}
        </Button>

        <Button
          variant="outlined"
          color="primary"
          startIcon={repairScanning ? <CircularProgress size={18} color="inherit" /> : <Search />}
          onClick={handleScanOnly}
          disabled={repairScanning || repairRepairing}
        >
          Scan Only (preview)
        </Button>
      </Box>

      {/* Result banner */}
      {repairResult && !repairScanning && !repairRepairing && (
        <>
          {repairResult.nothingMissing ? (
            <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight={600}>
                All batches are present — nothing to repair.
              </Typography>
              <Typography variant="caption">
                Every stock transfer line has a matching batch in the inventory.
              </Typography>
            </Alert>
          ) : repairResult.failed?.length === 0 ? (
            <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight={600}>
                Repair complete —{' '}
                {repairResult.repaired} missing batch{repairResult.repaired !== 1 ? 'es' : ''} re-recorded successfully.
              </Typography>
              {repairResult.stillMissing === 0 && (
                <Typography variant="caption">
                  Re-scan confirmed: all batches are now present in inventory.
                </Typography>
              )}
              {repairResult.stillMissing > 0 && (
                <Typography variant="caption" color="warning.main">
                  {repairResult.stillMissing} item{repairResult.stillMissing > 1 ? 's' : ''} still
                  missing — these have no inventory document and need the stock transfer re-run manually.
                </Typography>
              )}
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight={600}>
                Partial repair — {repairResult.repaired} fixed,{' '}
                {repairResult.failed.length} could not be repaired.
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                Failed: {repairResult.failed.join(', ')}
              </Typography>
            </Alert>
          )}
        </>
      )}

      {/* Missing batches table */}
      {missingBatches !== null && !repairScanning && !repairRepairing && (
        <>
          {missingBatches.length > 0 ? (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>{missingBatches.length}</strong> batch{missingBatches.length > 1 ? 'es' : ''} still missing across{' '}
                <strong>{new Set(missingBatches.map((m) => m.inventoryItemId)).size}</strong>{' '}
                inventory item{new Set(missingBatches.map((m) => m.inventoryItemId)).size > 1 ? 's' : ''}.
                {!repairResult && ' Click "Scan & Repair" to fix them.'}
              </Alert>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell><strong>Transfer #</strong></TableCell>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Product</strong></TableCell>
                      <TableCell><strong>Category</strong></TableCell>
                      <TableCell align="right"><strong>Qty</strong></TableCell>
                      <TableCell align="right"><strong>Cost Price</strong></TableCell>
                      <TableCell><strong>Issue</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {missingBatches.map((row, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>{row.transferNumber || '—'}</TableCell>
                        <TableCell>{row.transferDate || '—'}</TableCell>
                        <TableCell>{row.itemName}</TableCell>
                        <TableCell>{row.itemCategory || '—'}</TableCell>
                        <TableCell align="right">{row.qty}</TableCell>
                        <TableCell align="right">
                          {row.costPrice?.toLocaleString('en-LK', {
                            style: 'currency',
                            currency: 'LKR',
                            minimumFractionDigits: 2,
                          })}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={row.reason === 'inventory_item_missing' ? 'error' : 'warning'}
                            label={
                              row.reason === 'inventory_item_missing'
                                ? 'Inventory item missing'
                                : row.reason === 'batch_id_not_found'
                                ? 'Batch ID not in inventory'
                                : 'No batch for transfer'
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            !repairResult && (
              <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
                All transfer batches are present in inventory. No issues found.
              </Alert>
            )
          )}
        </>
      )}
    </Box>
  );
};

export default MigrationTool;
